/**
 * AppLauncher - Multi-application switcher (Google-style 9-dot grid).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  ChevronRight,
  ExternalLink,
  LayoutGrid,
  Shield,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import keycloak from '@/keycloak';

const CERT_URL = import.meta.env.VITE_CERT_URL || '';
const KIBO_URL = import.meta.env.VITE_KIBO_URL || '';
const IS_CERT_EXTERNAL = Boolean(CERT_URL) && CERT_URL !== window.location.origin;

type AppLink = {
  id: 'cert-community' | 'kibo';
  name: string;
  description: string;
  icon: LucideIcon;
  gradient: string;
  shadow: string;
  url: string;
  external: boolean;
};

const APPS: AppLink[] = [
  {
    id: 'cert-community',
    name: 'CERT Community',
    description: 'Cyber defense community, forums and advisories',
    icon: Shield,
    gradient: 'from-cyan-500 to-blue-600',
    shadow: 'shadow-cyan-500/20',
    url: IS_CERT_EXTERNAL ? `${CERT_URL}/community` : '/community',
    external: IS_CERT_EXTERNAL,
  },
  {
    id: 'kibo',
    name: 'BlueB',
    description: 'Internal incident management and analytics',
    icon: BarChart3,
    gradient: 'from-violet-500 to-purple-600',
    shadow: 'shadow-violet-500/20',
    url: KIBO_URL,
    external: true,
  },
];

export default function AppLauncher() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();
  const { user } = useAuthStore();

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleAppClick = (app: AppLink) => {
    setOpen(false);

    if (!user) {
      // Send the user through Keycloak SSO and land them on the target app.
      // External apps: full-page redirect via keycloak.login() so the
      // post-auth redirect lands on the other app's URL.
      void keycloak.login({ redirectUri: app.url });
      return;
    }

    if (app.external) {
      window.open(app.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(app.url);
    }
  };

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open applications menu"
        aria-expanded={open}
        className={cn(
          'h-9 w-9 rounded-xl transition-all',
          open ? 'bg-muted/80 text-foreground' : 'text-muted-foreground hover:bg-muted/80'
        )}
      >
        <LayoutGrid className="h-4 w-4" />
      </Button>

      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 mt-2 w-80 origin-top-right animate-in fade-in slide-in-from-top-2 duration-150 z-50"
        >
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl ring-1 ring-black/5">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
              <div>
                <h3 className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                  ETHIO CERT Systems
                </h3>
                {user?.email && (
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Signed in as <span className="font-medium text-foreground/80">{user.email}</span>
                  </p>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg p-1 text-muted-foreground hover:text-foreground hover:bg-muted/70 transition-colors"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-3 space-y-1.5">
              {APPS.map((app) => {
                const Icon = app.icon;
                return (
                  <button
                    key={app.id}
                    onClick={() => handleAppClick(app)}
                    className="group w-full flex items-center gap-3.5 rounded-xl px-3.5 py-3 text-left transition-all duration-150 hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <div
                      className={cn(
                        'flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200',
                        app.gradient,
                        app.shadow,
                      )}
                    >
                      <Icon size={20} className="text-white" strokeWidth={2} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-foreground truncate">
                          {app.name}
                        </span>
                        {app.external && (
                          <ExternalLink
                            size={11}
                            className="flex-shrink-0 text-muted-foreground group-hover:text-foreground/70"
                          />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {app.description}
                      </p>
                    </div>

                    <ChevronRight
                      size={16}
                      className="flex-shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground group-hover:translate-x-0.5 transition-all"
                    />
                  </button>
                );
              })}
            </div>

            <div className="px-5 py-2.5 border-t border-border bg-muted/20">
              <p className="text-[11px] text-muted-foreground text-center">
                Single sign-on - One account for all systems
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
